import { type FC, useMemo } from "react";
import { useBroadcastScore } from "../../../../hooks/use-broadcast-score";
import { useLineups } from "../../../../hooks/use-lineups";
import { useMatchMinute } from "../../../../hooks/use-match-minute";
import { toPlayerPhotoUrl, toProxiedMediaUrl } from "../../../../lib/media-proxy";
import type { Post } from "../../../../types/post.types";
import PostFooter from "../../components/post-footer/post-footer";
import PostHeader from "../../components/post-header/post-header";
import PostText from "../../components/post-text/post-text";
import { usePostBroadcastContext } from "../../post-broadcast.context";
import * as CSS from "./goal-post.styles";

type GoalPostProps = {
	post: Post;
};

const GoalPost: FC<GoalPostProps> = ({ post }) => {
	const { homeTeam, homeLogo, awayTeam, awayLogo, broadcastId, fixtureId } =
		usePostBroadcastContext();

	const lineups = useLineups(fixtureId);

	const minute = useMatchMinute(post.date, post.minute);
	const { homeScore, awayScore } = useBroadcastScore(broadcastId);

	const scorer = useMemo(() => {
		if (!lineups || !post.team || post.scorerId == null) return undefined;

		const teamLineup = lineups[post.team];
		return [...teamLineup.startXI, ...teamLineup.substitutes].find(
			(player) => player.id === post.scorerId,
		);
	}, [lineups, post.team, post.scorerId]);

	return (
		<CSS.Post>
			<PostHeader
				time={minute}
				type={post.type}
				logo={
					post.team
						? toProxiedMediaUrl(post.team === "home" ? homeLogo : awayLogo)
						: undefined
				}
			/>
			<div
				style={{
					marginLeft: 44,
					display: "flex",
					flexDirection: "column",
					gap: "0.3rem",
				}}
			>
				<CSS.Title>Goal!</CSS.Title>
				<CSS.Score>
					{homeTeam} {homeScore} - {awayScore} {awayTeam}
				</CSS.Score>
				{scorer && (
					<CSS.PlayerRow>
						<img width="32" src={toPlayerPhotoUrl(scorer.id)} alt="" />
						<CSS.Text>{scorer.name}</CSS.Text>
					</CSS.PlayerRow>
				)}
				<PostText postId={post.id} text={post.text} />
				<PostFooter postId={post.id} postType={post.type} />
			</div>
		</CSS.Post>
	);
};

export default GoalPost;
